import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAxios from "../../../../MainComponents/Hooks/useAxios.jsx";
import { useAuthState } from "../../../../MainComponents/GlobalContext.jsx";
import HelmetTags from "../../../../MainComponents/HelmetTags.jsx";
import Loader from "../../../../MainComponents/Loader.jsx";
import { Modal, Tag } from "antd";

// ─── Permission helpers ───────────────────────────────────────────────────────

function hasPermission(admin, ...prefixes) {
  if (admin?.roles?.[0]?.name === "superadmin") return true;
  return prefixes.some((prefix) =>
    admin?.permissions?.some((p) => p.name.startsWith(prefix)),
  );
}

function canDelete(admin) {
  return hasPermission(
    admin,
    "questionbank_deletequestion",
    "lecture_addquiz",
    "lecture_addHw",
  );
}

function canEdit(admin) {
  return hasPermission(
    admin,
    "questionbank_editquestion",
    "lecture_addquiz",
    "lecture_addHw",
  );
}

// ─── Edit route resolver ──────────────────────────────────────────────────────

function resolveEditRoute(type, isEssay, questionKey, categoryID) {
  const variant = isEssay ? "essay" : "choose";
  const typeRoutes = {
    homework: `/admin/lectures/sessions/homework/edit-${variant}-question/${questionKey}`,
    quiz: `/admin/lectures/sessions/quiz/edit-${variant}-question/${questionKey}`,
    exam: `/admin/exams/edit-${variant}-question/${questionKey}`,
  };
  return (
    typeRoutes[type] ??
    `/admin/question-bank/${categoryID}/edit-${variant}-question/${questionKey}`
  );
}

// ─── Answer ───────────────────────────────────────────────────────────────────

const ANSWER_LABELS = {
  answer1: "First answer",
  answer2: "Second answer",
  answer3: "Third answer",
  answer4: "Fourth answer",
};

function Answer({ answerKey, html, isCorrect }) {
  return (
    <div
      className={`categorySingleAnswer ${answerKey}Wrapper${isCorrect ? " correctSingleAnswer" : ""}`}
    >
      <p>{ANSWER_LABELS[answerKey]}</p>
      {/* Skip the first child (empty paragraph injected by the rich-text editor) */}
      <div
        dangerouslySetInnerHTML={{
          __html: html,
        }}
      />
    </div>
  );
}

// ─── QuestionCard ─────────────────────────────────────────────────────────────

function QuestionCard({ question, type, categoryID, admin, onDeleteRequest }) {
  const navigate = useNavigate();

  const showDelete = canDelete(admin);
  const showEdit = canEdit(admin);

  function handleEdit() {
    const route = resolveEditRoute(
      type,
      question.is_essay,
      question.key,
      categoryID,
    );
    const state = type ? { state: { lectureQuestion: true } } : undefined;
    navigate(route, state);
  }

  return (
    <div className="singleQuestionWrapper">
      <div className="optionsQuestionWrapper">
        <div className="questionOptions">
          {showDelete && (
            <button
              className="deleteQuestionBtn"
              onClick={() => onDeleteRequest(question.key)}
            >
              Delete
            </button>
          )}
          {showEdit && (
            <button className="editQuestionBtn" onClick={handleEdit}>
              Edit
            </button>
          )}
        </div>
        <div className="degreeQuestionWrapper">
          <span className="degree">{question.degree} Degree</span>
        </div>

        <div className="categoryQuestionWrapper">
          <div className="essayTitleWrapper">
            {type === null && question.difficulty && (
              <h4>{question.difficulty}</h4>
            )}
            {question.is_essay === 1 && <span>Essay question</span>}
            <p>Question No. {question.n}</p>
          </div>
          <div
            dangerouslySetInnerHTML={{
              __html: question.question,
            }}
          />
        </div>
      </div>

      {!question.is_essay && (
        <div className="categoryAnswersWrapper">
          {["answer2", "answer1", "answer4", "answer3"].map((key) => (
            <Answer
              key={key}
              answerKey={key}
              html={question[key]}
              isCorrect={question.correct_answer === key}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

function QuestionBankCategory({
  type,
  QuizQuestionsData,
  refetchQuiz,
  setRefetchQuiz,
  id,
}) {
  const { userData: { admin } = {} } = useAuthState();
  const { categoryID } = useParams();

  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [reFetch, setReFetch] = useState(false);
  const [submitDelete, setSubmitDelete] = useState({
    flag: "",
    dependency: false,
    data: {},
  });

  // ── Data fetching ──────────────────────────────────────────────────────────

  const [allQuestionsData, allQuestionsErrors, loading] = useAxios(
    `${process.env.REACT_APP_CATEGORY_QUESTIONS_API}${categoryID}`,
    "GET",
    !type, // only fetch when not in quiz/homework/exam mode
    reFetch,
  );

  const [deleteQuestionSuccess] = useAxios(
    process.env.REACT_APP_DELETE_QUESTION_API,
    "POST",
    submitDelete.flag,
    submitDelete.dependency,
    submitDelete.data,
    true,
  );

  // ── Side-effects ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (!deleteQuestionSuccess) return;
    type ? setRefetchQuiz(!refetchQuiz) : setReFetch((v) => !v);
    setPendingDeleteId(null);
  }, [deleteQuestionSuccess]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handleDeleteRequest(key) {
    setPendingDeleteId(key);
  }

  function handleDeleteConfirm() {
    setSubmitDelete({
      flag: "deleteQuestion",
      dependency: !submitDelete.dependency,
      data: { id: pendingDeleteId },
    });
  }

  function handleDeleteCancel() {
    setPendingDeleteId(null);
  }

  // ── Derived data ───────────────────────────────────────────────────────────

  const questions =
    QuizQuestionsData?.length > 0
      ? QuizQuestionsData
      : (allQuestionsData?.data ?? []);

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading && !type) return <Loader />;

  return (
    <section
      className={`h-full w-full overflow-y-auto ${!type && "px-20"} py-20 dark:bg-dark dark:text-light sm:px-10`}
    >
      {!type && <HelmetTags title="Question Bank | Mr Ahmed Sabry" />}

      <Modal
        open={pendingDeleteId !== null}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        centered
        title="Confirm Delete?"
        okText="Confirm"
        cancelText="Cancel"
        className="customModal"
      ></Modal>

      {!type && (
        <h2 className="mb-16 w-full text-start text-4xl font-bold sm:text-center">
          Category Questions {allQuestionsData?.name}
        </h2>
      )}

      <div className="all-questions flex h-auto w-full flex-col gap-14 px-10">
        {!type &&
          allQuestionsErrors &&
          typeof allQuestionsErrors === "string" && (
            <p className="w-full text-center">{allQuestionsErrors}</p>
          )}

        {questions.map((question) => (
          <QuestionCard
            key={question.key}
            question={question}
            type={type}
            categoryID={categoryID}
            admin={admin}
            onDeleteRequest={handleDeleteRequest}
          />
        ))}
      </div>
    </section>
  );
}

export default QuestionBankCategory;
